import './styles/CoursesList.scss';

import { useApolloClient } from '@apollo/client';
import { CloudDownload, Create } from '@mui/icons-material';
import {
  Button,
  Grid,
  Icon,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Tab,
  Tabs
} from '@mui/material';
import { Box } from '@mui/system';

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import USER_QUERY from 'gql/queries/currentUser.gql';

import ContextMenu from 'app/components/ContextMenu';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Modal } from 'app/components/Modal';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import usePageHeading from 'utils/hooks/usePageHeading';

import { IOCourse } from '../IOCourse';
import { IOCourseAdminList } from './components/IOCourseAdminList';
import { IOCourseStudentList } from './components/IOCourseStudentList';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      flexGrow={1}
      role="tabpanel"
      hidden={value !== index}
      id={`${index === 0 ? 'all' : 'enrolled'}-course-list`}
      aria-labelledby={`${index === 0 ? 'all' : 'enrolled'}-course-list`}
      {...other}
    >
      {children}
    </Box>
  );
}

export function CoursesList() {
  const { t } = useTranslation();
  const client = useApolloClient();

  const anchorRef = useRef(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [reportMenuOpen, setReportMenuOpen] = useState(false);

  const [activeTab, setActiveTab] = useState(0);

  const { currentUser } = client.readQuery({ query: USER_QUERY });

  const aclHelper = new ACLHelper(currentUser);

  usePageHeading({
    className: 'courses-management',
    heading: t(translations.pages.internationalOffice.coursesTitleLong()),
    mobilePadding: false
  });

  const ReportMenu = () => {
    return (
      <ContextMenu
        open={reportMenuOpen}
        setOpen={setReportMenuOpen}
        anchorEl={anchorRef.current}
        className="courses-management__contextmenu"
      >
        <MenuItem>
          <Grid
            container
            className="courses-management__contextmenu__container"
            onClick={() => {
              window.open(`${import.meta.env.VITE_GATEWAY_URL}/reports/officers/courses`, '_blank');
            }}
          >
            <Grid item xs={3}>
              <ListItemIcon>
                <Icon
                  className="fas fa-user"
                  style={{
                    width: 'auto'
                  }}
                />
              </ListItemIcon>
            </Grid>
            <Grid item xs={9}>
              <ListItemText
                primary={t(translations.pages.internationalOffice.courses.officersReport())}
              />
            </Grid>
          </Grid>
        </MenuItem>
        <MenuItem>
          <Grid
            container
            className="courses-management__contextmenu__container"
            onClick={() => {
              window.open(
                `${import.meta.env.VITE_GATEWAY_URL}/reports/faculties/courses`,
                '_blank'
              );
            }}
          >
            <Grid item xs={3}>
              <ListItemIcon>
                <Icon
                  className="fas fa-university"
                  style={{
                    width: 'auto'
                  }}
                />
              </ListItemIcon>
            </Grid>
            <Grid item xs={9}>
              <ListItemText
                primary={t(translations.pages.internationalOffice.courses.facultyReport())}
              />
            </Grid>
          </Grid>
        </MenuItem>
      </ContextMenu>
    );
  };

  return aclHelper.isIncomingsAdmin() ? (
    <>
      <ErrorBoundary boundaryTag="ioCourseMangegementBoundary">
        <Modal
          open={createModalOpen}
          onClose={(event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setCreateModalOpen(false);
          }}
          headline={t(translations.pages.internationalOffice.courses.newIOCourse())}
          onCancel={() => setCreateModalOpen(false)}
          disabledCancel={disabledCancel}
        >
          <IOCourse
            closeHandler={() => {
              setCreateModalOpen(false);
            }}
            setDisabledCancel={setDisabledCancel}
          />
        </Modal>
      </ErrorBoundary>
      <Grid
        style={{
          display: 'flex',
          justifyContent: 'flex-end',

          alignItems: 'center',
          paddingBottom: '1em'
        }}
        container
      >
        <Button
          startIcon={<CloudDownload />}
          variant="outlined"
          color="primary"
          className="courses-management__actions--secondary"
          onClick={() => {
            setReportMenuOpen(true);
          }}
          ref={anchorRef}
        >
          {t(translations.pages.internationalOffice.courses.reports())}
        </Button>
        <Button
          startIcon={<Create />}
          variant="contained"
          className="courses-management__actions--primary"
          onClick={() => setCreateModalOpen(true)}
        >
          {t(translations.common.create())}
        </Button>
        <ReportMenu />
      </Grid>
      <Paper className="courses-management__paper">
        <IOCourseAdminList />
      </Paper>
    </>
  ) : (
    <Paper className="courses-management__paper">
      <Tabs
        indicatorColor="primary"
        value={activeTab}
        onChange={(e, newVal) => setActiveTab(newVal)}
        className="invoice-management__tabs"
      >
        <Tab label={t(translations.pages.internationalOffice.coursesTitle())} />
        <Tab label={t(translations.pages.internationalOffice.myCourses())} />
      </Tabs>
      <TabPanel value={activeTab} index={0}>
        <IOCourseStudentList />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <IOCourseStudentList showOnlyEnrolled />
      </TabPanel>
    </Paper>
  );
}
